import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useDispatch, useSelector } from 'react-redux';
import styles from './Series3UpgradeV2.module.scss';
import valuePropStyles from '../ProductDetails/ProductDetails.module.scss';
import ValuePropsBox, { ValueProp } from '../ProductDetails/Series3CollarDetails/components/ValuePropsBox';
import { ReactComponent as IconCompact } from '../../assets/images/icons/icon_compact.svg';
import { ReactComponent as IconBroadcast } from '../../assets/images/icons/icon_broadcast.svg';
import { ReactComponent as IconLte } from '../../assets/images/icons/icon_lte.svg';
import { ReactComponent as IconReinforced } from '../../assets/images/icons/icon_reinforced.svg';
import { ReactComponent as IconDrip } from '../../assets/images/icons/icon_drip.svg';
import { ReactComponent as IconLightning } from '../../assets/images/icons/icon_lightning.svg';
import classNames from 'classnames';
import { Redirect, useHistory, useLocation } from 'react-router-dom';
import Series3UpgradeContext from '../../contexts/Series3UpgradeContext';
import AppPaths from '../../AppPaths';
import { cartActions as upgradeCartActions } from '../../reducers/series3UpgradeCart';
import { series3UpgradeShopActions } from '../../reducers/series3UpgradeShop';
import * as types from '../../types';

function getWhatsNewValueProps(t: TFunction): ValueProp[] {
  return [
    {
      icon: <IconBroadcast />,
      text: (
        <div className={classNames(valuePropStyles.valueProp, valuePropStyles.valuePropCheat)}>
          <span className={valuePropStyles.valuePropBadge}>{t('new')}</span>{' '}
          <span className={valuePropStyles.valuePropHeader}>{t('real_time_live_gps')}</span>{' '}
          <span className={valuePropStyles.valuePropBody}>{t('our_fastest_and_most_advanced_tracking')}</span>
        </div>
      ),
    },
    {
      icon: <IconCompact />,
      text: (
        <div className={classNames(valuePropStyles.valueProp, valuePropStyles.valuePropCheat)}>
          <span className={valuePropStyles.valuePropBadge}>{t('new')}</span>{' '}
          <span className={valuePropStyles.valuePropHeader}>{t('50_percent_smaller')}</span>{' '}
          <span className={valuePropStyles.valuePropBody}>
            {t('low_profile_design_to_better_fit_dogs_of_all_sizes')}
          </span>
        </div>
      ),
    },
    {
      icon: <IconLte />,
      text: (
        <div className={valuePropStyles.valueProp}>
          <span className={valuePropStyles.valuePropHeader}>{t('unlimited_gps_lte_m_coverage')}</span>{' '}
          <span className={valuePropStyles.valuePropBody}>{t('track_in_most_remote_areas_and_on_hikes')}</span>
        </div>
      ),
    },
    {
      icon: <IconReinforced />,
      text: (
        <div className={valuePropStyles.valueProp}>
          <span className={valuePropStyles.valuePropHeader}>{t('stainless_steel_frame')}</span>{' '}
          <span className={valuePropStyles.valuePropBody}>{t('built_for_up_to_500_lb_of_tug_resistance')}</span>
        </div>
      ),
    },
    {
      icon: <IconDrip />,
      text: (
        <div className={valuePropStyles.valueProp}>
          <span className={valuePropStyles.valuePropHeader}>{t('ip68_ip66k')}</span>{' '}
          <span className={valuePropStyles.valuePropBody}>{t('more_waterproof_than_ever_before')}</span>
        </div>
      ),
    },
    {
      icon: <IconLightning />,
      text: (
        <div className={valuePropStyles.valueProp}>
          <span className={valuePropStyles.valuePropHeader}>{t('long_battery_life')}</span>{' '}
          <span className={valuePropStyles.valuePropBody}>{t('up_to_3_months_from_one_charge')}</span>
        </div>
      ),
    },
  ];
}

export default function Series3UpgradeV2() {
  const { t } = useTranslation();
  const history = useHistory();
  const upgradeContext = useContext(Series3UpgradeContext);
  const series3UpgradeCart = useSelector((state: types.AppState) => state.series3UpgradeShop.cart);
  const dispatch = useDispatch();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const moduleId = searchParams.get('moduleId');

  // Since we upgrade one pet at a time, clear out the cart when landing here just in case someone went through
  // checkout halfway but didn't complete and re-started with a different pet.
  useEffect(() => {
    // Clear cart and checkout progress upon mount
    if (Object.keys(series3UpgradeCart.cartItems).length > 0) {
      dispatch(series3UpgradeShopActions.resetCheckout());
      dispatch(upgradeCartActions.resetCart());
    }
  }, [dispatch, series3UpgradeCart.cartItems, series3UpgradeCart.couponCode]);

  if (!upgradeContext || !moduleId || !upgradeContext.upgradeV2ModuleIds.includes(moduleId)) {
    return <Redirect to={AppPaths.NotFound} />;
  }

  return (
    <div className={classNames('black-friday-upgrade', styles.upgradeScreenContainer)}>
      <div className={styles.offerTag}>{t('cyber_monday_offer_caps')}</div>
      <div className={styles.header}>{t('upgrade_and_save')}</div>
      <div className={styles.description}>{t('unleash_better_performance_in_more_durable_sleeker_collar')}</div>
      <div className={styles.offerContainer}>
        <div className={styles.offerTitle}>{t('cyber_monday_offer_title')}</div>
        <div className={styles.offerPricing}>
          <div className={styles.dealPrice}>{'$99'}&nbsp;</div>
          <div className={styles.regularPrice}>{'$189'}</div>
        </div>
        <div className={styles.offerBulletPoints}>
          <div className={styles.offerBulletPoint}>
            {'\u00A0•\u00A0'}
            {t('new_series_3_collar')}
          </div>
          <div className={styles.offerBulletPoint}>
            {'\u00A0•\u00A0'}
            {t('one_year_fi_membership')}
          </div>
        </div>
      </div>
      <div className={styles.collarImageContainer} />
      <div className={styles.whatsNewHeader}>{t('series_3_whats_new')}</div>
      <div className={styles.valuePropsContainer}>
        <ValuePropsBox valueProps={getWhatsNewValueProps(t)} />
      </div>
      <button
        className={styles.getSeriesThreeButton}
        onClick={() => history.push(AppPaths.Series3UpgradeV2.ProductSelector(moduleId))}
      >
        {t('get_series_3')}
      </button>
      <div className={styles.disclaimer}>
        <div>{t('s3_membership_replaces_your_s2_subscription', { amount: 189 })}</div>
      </div>
    </div>
  );
}
